import HttpService from "~/Scripts/services/httpService";

const instance = new HttpService( "umbraco/api/EventsApi/" );
const basketStepInstance = new HttpService( "umbraco/api/BasketStep/" );

/* eslint-disable max-len */
/* eslint-disable arrow-body-style */

/**
 * @typedef {Object} BookingValidationModel
 * @property {string} [orderLineId] UUID The order line associated with the booking
 * @property {string} [bookingId] UUID The ID of the booking being validated
 * @property {boolean} valid Is the booking valid?
 * @property {boolean} bookingHasNotBeenAbandoned Determine if the booking has been abandoned
 * @property {boolean} notPastLastBookingDate Determine if the booking is passed it's LastBookingDate
 * @property {boolean} bookingHasNotExpired Ensures the booking expiry date/time hasn't passed
 * @property {boolean} allDelegateDetailsProvided Determine if all delegate details have been provided (name, email, etc)
 * @property {boolean} allReservedDelegatesProvided Determine if the number of reserved delegates matches the number of delegates we have saved
 * @property {boolean} matchesOrderLineQuantity Determine if the order line has the correct quantity set
 */

/**
 * @typedef {Object} BookingListValidationModel A model for determining the validity of a list of event bookings
 * @property {boolean} valid whether all of the contained bookings are valid
 * @property {Array<BookingValidationModel>} bookings The validation results of each booking
 */

/**
 * List event instances
 * @param {any} eventId The event ID
 * @param {any} pricingScheduleId The selected pricing schedule ID
 */
export const listInstances = ( eventId, pricingScheduleId ) => {
  return instance.get( "ListInstances", { eventId, pricingScheduleId } );
};

/**
 * Gets an event instance by ID
 * @param {any} instanceId The instance ID
 * @param {any} pricingScheduleId The pricing schedule ID
 */
export const getInstance = ( instanceId, pricingScheduleId ) => {
  return instance.get( "GetInstance", { instanceId, pricingScheduleId } );
};

/**
 * List event bookings in the basket
 * @param {any} orderHeaderId The order header ID
 */
export const listBookings = ( orderHeaderId ) => {
  return instance.get( "ListBookings", { orderHeaderId } );
};

/**
 * Load an event booking
 * @param {any} bookingId The booking ID to use
 */
export const loadBooking = ( bookingId ) => {
  return instance.get( "LoadBooking", { bookingId } );
};

/**
 * List disabilities
 */
export const listDisabilities = () => {
  return instance.get( "ListDisabilities" );
};

/**
 * List dietary requirements
 */
export const listDietaryRequirements = () => {
  return instance.get( "ListDietaryRequirements" );
};

/**
 * List job types for a given event ID
 * @param {any} instanceId The instance ID to use
 */
export const listEventJobTypes = ( instanceId ) => {
  return instance.get( "ListEventJobTypes", { instanceId } );
};

/**
 * List job types for a given event ID with all job types as a default
 * @param {any} instanceId The instance ID to use
 */
export const listEventOrAllJobTypes = ( instanceId ) => {
  return instance.get( "listEventOrAllJobTypes", { instanceId } );
};

/**
 * List all the person job types
 * @param {any} instanceId The instance ID to use
 */
export const listPersonJobTypes = () => {
  return instance.get( "ListPersonJobTypes" );
};

/**
 * List instance types
 */
export const listInstanceTypes = () => {
  return instance.get( "ListInstanceTypes" );
};

/**
 * Load instance filters
 */
export const loadInstanceFilters = () => {
  return instance.get( "Filters" );
};

/**
 * Start the booking process for the given event instance
 * @param {any} instanceId The event instance ID
 * @param {any} orderLineId The associated product order line ID
 * @param {any} numDelegates The number of delegates to reserve
 * @param {any} wishlistBooking Whether this is a wishlist booking or not
 * @param {any} eventBookingId The event booking ID
 */
export const startBooking = ( instanceId, orderLineId, numDelegates,
  wishlistBooking, eventBookingId ) => {
  return instance.post( "StartBooking", null, {
    instanceId,
    orderLineId,
    numDelegates,
    wishlistBooking,
    eventBookingId
  } );
};

/**
 * Update an event booking
 * @param {any} instanceId The event instance ID
 * @param {any} bookingId The event booking ID
 * @param {any} webAccountId The web account ID
 * @param {string} statusCode The event status code
 * @param {any} orderLineId The order line ID
 * @param {any} orgId The org ID
 * @param {Number} numDelegates The number of event delegates
 */
export const updateBooking = ( instanceId, bookingId, webAccountId, statusCode,
  orderLineId, orgId, numDelegates ) => {
  return instance.post( "UpdateBooking", null, {
    instanceId,
    bookingId,
    webAccountId,
    statusCode,
    orderLineId,
    orgId,
    numDelegates
  } );
};

/**
 * Check if any bookings in the basket are valid
 * @returns {{data: BookingListValidationModel}}
 */
export const validateBookings = () => {
  return basketStepInstance.get( "ValidateBookings" );
};

/**
 * Start the booking process for the given event instance
 * @param {any} instanceId The event instance ID
 * @param {any} payload The waiting list form data
 */
export const joinWaitingList = ( instanceId, payload ) => {
  return instance.post( "JoinWaitingList", payload, {
    instanceId
  } );
};

/**
 * Save the given delegates against the given booking
 * @param {} bookingId The booking to save the delegates against
 * @param {} delegates The delegates to save
 */
export const saveDelegates = ( bookingId, delegates ) => {
  return instance.post( "SaveDelegates", delegates, { bookingId } );
};

/**
 * Abandons the given booking
 * @param {} bookingId The booking to abandon
 */
export const abandonBooking = ( bookingId ) => {
  return instance.delete( "AbandonBooking", { bookingId } );
};

/**
 * Load Event delegate meta data
 * @param {GUID} eventInstanceId The event instance ID to load delegate meta data for
 */
export const loadDelegateMetadata = ( eventInstanceId ) => instance.get( "LoadDelegateMetadata", { eventInstanceId } );

/**
 * List Event delegate types metadata
 * @param {GUID} eventInstanceId The event instance ID to load delegate types for
 */
export const listDelegateTypesMetadata = ( eventInstanceId ) => instance.get( "ListDelegateTypesMetadata", { eventInstanceId } );

/**
 * Save delegate meta data
 * @param {object} metadata Meta data to save
 */
export const saveDelegateMetadata = ( metadata ) => instance.post( "SaveDelegateMetadata", metadata );

/**
 * Load delegate booking meta data
 * @param {GUID} bookingId The booking ID to load meta data for
 */
export const loadBookingDelegateMetadata = ( bookingId ) => instance.get( "LoadBookingDelegateMetadata", { bookingId } );
